/** @jsxImportSource @emotion/react */
import { Container, Divider, Flex, Grid, Text } from "basis";
import { ErrorBoundary } from "components/Error/ErrorBoundary";
import PropTypes from "prop-types";
import { Children } from "react";
import { MaxWidthContainer } from "../MaxWidthContainer/MaxWidthContainer";
import { SvgInline } from "../SvgInline/SvgInline";

function Header({ icon, children }) {
  return (
    <Container bg="secondary.lightBlue.t15" padding="0 4" padding-xs="0 10">
      <MaxWidthContainer>
        <Container padding={icon ? "2 0" : "10 0"}>
          <Flex placeItems="left center">
            <Text
              as="h1"
              textStyle="heading5"
              textStyle-sm="heading4"
              color="primary.blue.t100"
              margin={icon ? "0 8 0 0" : "0"}
            >
              {children}
            </Text>
            {icon && <SvgInline name={icon} size="92" />}
          </Flex>
        </Container>
      </MaxWidthContainer>
    </Container>
  );
}

Header.propTypes = {
  icon: PropTypes.string,
  children: PropTypes.node.isRequired,
};

function Main({ children, shouldRenderContent = true }) {
  return shouldRenderContent ? children : null;
}

Main.propTypes = {
  children: PropTypes.node.isRequired,
};

function Sidebar({ children }) {
  return children;
}

Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
};

function Layout({ children }) {
  const { header, main, sidebar } = Children.toArray(children).reduce(
    (acc, child) => {
      switch (child.type) {
        case Header: {
          if (acc.header) {
            throw new Error("Layout can't have multiple Header children.");
          } else {
            acc.header = child;
          }
          return acc;
        }

        case Main: {
          if (acc.main) {
            throw new Error("Layout can't have multiple Main children.");
          } else {
            acc.main = child;
          }
          return acc;
        }

        case Sidebar: {
          if (acc.sidebar) {
            throw new Error("Layout can't have multiple Sidebar children.");
          } else {
            acc.sidebar = child;
          }
          return acc;
        }

        default: {
          return acc;
        }
      }
    },
    {},
  );

  return (
    <>
      {header}
      {(main || sidebar) && (
        <Container margin-lg="0 10">
          <MaxWidthContainer>
            <Container
              margin={sidebar ? "8 0 0 0" : "8 0 8 0"}
              padding-lg="0 0 10"
            >
              <Grid preset="page" rowsGap="10">
                {main && (
                  <Grid.Item colSpan="all" rowSpan="0" colSpan-lg="0-7">
                    <Container margin="0 4" margin-xs="0 10" margin-lg="0">
                      <ErrorBoundary>{main}</ErrorBoundary>
                    </Container>
                  </Grid.Item>
                )}
                {sidebar && (
                  <Grid.Item
                    colSpan="all"
                    rowSpan="1"
                    colSpan-lg="8-11"
                    rowSpan-lg="0"
                  >
                    <Container bg="grey.t03" bg-lg="transparent">
                      <Container hide-lg>
                        <Divider margin="1 0 0 0" />
                      </Container>
                      <Container padding="8 4" padding-xs="8 10" padding-lg="0">
                        {sidebar}
                      </Container>
                    </Container>
                  </Grid.Item>
                )}
              </Grid>
            </Container>
          </MaxWidthContainer>
        </Container>
      )}
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

Layout.Header = Header;
Layout.Main = Main;
Layout.Sidebar = Sidebar;

export { Layout };
